/* PDP personalization progress + guidance — v3 (2026-10-04; first version 2026-10-02).
   Theme-owned nodes only: the "Make it yours" header prepended inside the CAF shell `.caf-pdp-custom-panel`,
   a generated <style id="caf-pdp-progress-style"> in <head>, the photo-upload veil inside `.product__images`
   and `html.caf-pdp-typing`. READ-ONLY on TeeInBlue DOM: never moves, renames, re-renders or writes to app
   nodes (pages/_app-teeinblue.md "Do not move TeeInBlue option DOM").

   Counting rules (v3 pdp.html notes 7 / 38):
   - visible `#teeFormInputs .tee-field` with its own `.tee-asterisk` (inputs carry no `required`);
   - choice fields the app pre-selects (design colour swatch, default select value) are excluded, so a
     fresh page reads "0 of 7", not "1 of 8";
   - photo fields count, complete once the app shows its uploaded `img.tee-photo-image`;
   - searchable dropdowns count, complete once the current value is not the placeholder.
   Guidance: after a failed Add to cart / Buy now click the header shows "N details still needed", the first
   missing field scrolls in BELOW the sticky header (`--caf-sticky-header-h`, + the pinned phone preview) and
   gets focus; TeeInBlue's own validation still runs (we never block it). Its stale `.tee-field--invalid` marks
   are cleared visually as soon as a field is complete (generated per-field CSS keyed on the app's field ids).
   The phone sticky bar's "Finish personalizing" uses focusFirstIncomplete() only (no red marks).
   Also: max-length callout, photo-upload veil, dropdown "no matches" text, html.caf-pdp-typing (phones).
   Public API: window.CAFPdpPersonalizeProgress { getState, focusFirstIncomplete, markAttempt, refresh }.
   Event: `caf:personalize-progress` { done, total, complete, missing, attempted }. */
(() => {
  if (window.CAFPdpPersonalizeProgress) return;

  const doc = document;
  const root = doc.documentElement;
  const PANEL_SELECTOR = '.caf-pdp-custom-panel';
  const FORM_SELECTOR = '#teeFormInputs';
  const FIELD_SELECTOR = '#teeFormInputs .tee-field';
  const DETAILS_SELECTOR = '.product__details--product-page';
  const HEADER_CLASS = 'caf-pdp-progress';
  const STYLE_ID = 'caf-pdp-progress-style';
  const EVENT_NAME = 'caf:personalize-progress';
  const TEEINBLUE_EVENT_COMPONENT_INJECTED = 'teeinblue-event-component-injected';
  // 2026-10-13: on a Shopify listing linked to TeeInBlue ("platform product") the app's fields sit in the native
  // form — no #teeFormActions; the buttons are the theme's own Add to cart / Buy now.
  const PLATFORM_CLASS = 'teeinblue-platform-product-enabled';
  const isPlatform = () => doc.body.classList.contains(PLATFORM_CLASS);
  const ATTEMPT_BUTTONS =
    '#teeAtcButton, #teeBuynowButton, .caf-pdp-custom-panel button.add_to_cart, .caf-pdp-custom-panel [data-caf-buy-now]';
  const TEXT_CONTROL = 'textarea, input[type="text"], input:not([type])';
  const CHOICE_KINDS = ['choice', 'select', 'custom-select'];
  const MAXLENGTH_CALLOUT_MIN = 8; // hitting a 2–4 character limit (date, year) is normal, not worth a callout

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqMobile = window.matchMedia('(max-width: 798px)');

  const ICONS = {
    sparkles:
      '<path d="M9.94 15.5a2 2 0 0 0-1.44-1.44l-6.13-1.58a.5.5 0 0 1 0-.96L8.5 9.94A2 2 0 0 0 9.94 8.5l1.58-6.14a.5.5 0 0 1 .96 0l1.58 6.14a2 2 0 0 0 1.44 1.44l6.14 1.58a.5.5 0 0 1 0 .96l-6.14 1.58a2 2 0 0 0-1.44 1.44l-1.58 6.14a.5.5 0 0 1-.96 0z"/><path d="M20 3v4M22 5h-4"/>',
    info: '<circle cx="12" cy="12" r="9.5"/><path d="M12 11v5.5M12 7.6v.4"/>',
    check: '<path d="M20 6 9 17l-5-5"/>',
    alert: '<circle cx="12" cy="12" r="9.5"/><path d="M12 7.5v5.5M12 16.4v.4"/>',
    down: '<path d="m6 9 6 6 6-6"/>',
  };
  const icon = (name, cls = '') =>
    `<svg class="caf-pdp-progress__icon${cls ? ` ${cls}` : ''}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${ICONS[name]}</svg>`;

  /* ------------------------------------------------------------------ field reading (app DOM, read-only) */

  // Controls that belong to this field, not to a nested `.tee-field__subfield` field.
  const own = (field, selector) =>
    Array.from(field.querySelectorAll(selector)).filter((node) => node.closest('.tee-field') === field);

  const isVisible = (field) => {
    if (!(field instanceof HTMLElement) || field.classList.contains('sr-only')) return false;
    const rect = field.getBoundingClientRect();
    return field.getClientRects().length > 0 && rect.width > 1 && rect.height > 1;
  };

  const isRequired = (field) => own(field, '.tee-asterisk').length > 0;

  const fieldKind = (field) => {
    if (field.classList.contains('tee-field--photo') || own(field, 'input[type="file"]').length) return 'photo';
    if (own(field, '.tee-custom-select').length) return 'custom-select';
    if (own(field, 'select').length) return 'select';
    if (own(field, TEXT_CONTROL).some((control) => !control.classList.contains('tee-custom-select__search-input'))) return 'text';
    if (own(field, 'input[type="radio"], input[type="checkbox"]').length) return 'choice';
    return 'unknown';
  };

  // true / false, or null when completion is not readable from the DOM (left to TeeInBlue validation).
  const isComplete = (field, kind) => {
    switch (kind) {
      case 'photo':
        return own(field, 'img.tee-photo-image').some((img) => Boolean(img.getAttribute('src')));
      case 'custom-select': {
        const current = own(field, '.tee-custom-select__current')[0];
        return Boolean(current && !current.classList.contains('tee-custom-select__placeholder') && current.textContent.trim());
      }
      case 'select': {
        const select = own(field, 'select')[0];
        const option = select && select.options[select.selectedIndex];
        return Boolean(option && option.value !== '' && option.value !== 'null' && !option.disabled);
      }
      case 'text':
        return own(field, TEXT_CONTROL).some(
          (control) => !control.classList.contains('tee-custom-select__search-input') && control.value.trim() !== '',
        );
      case 'choice':
        return own(field, 'input[type="radio"], input[type="checkbox"]').some((choice) => choice.checked);
      default:
        return null;
    }
  };

  const anonymousKeys = new WeakMap();
  let anonymousCounter = 0;
  const keyOf = (field) => {
    if (field.id) return field.id;
    if (!anonymousKeys.has(field)) {
      anonymousCounter += 1;
      anonymousKeys.set(field, `caf-anon-${anonymousCounter}`);
    }
    return anonymousKeys.get(field);
  };

  // Choice fields the shopper never touched but the app already completed when they first appeared
  // (default swatch, default select value) are "preset" and excluded from the count — permanently, so the
  // total never jumps when the shopper later changes that choice.
  const presets = new Map();
  const touched = new Set();

  const markTouched = (event) => {
    if (!event.isTrusted || !(event.target instanceof Element)) return;
    let field = event.target.closest(FIELD_SELECTOR);
    while (field) {
      touched.add(keyOf(field));
      field = field.parentElement && field.parentElement.closest(FIELD_SELECTOR);
    }
  };

  const getState = () => {
    let done = 0;
    let total = 0;
    let firstIncomplete = null;
    const missing = [];
    const completed = [];
    let hasPhoto = false;
    let hasTyping = false;

    doc.querySelectorAll(FIELD_SELECTOR).forEach((field) => {
      if (!isVisible(field) || !isRequired(field)) return;
      const kind = fieldKind(field);
      const complete = isComplete(field, kind);
      if (complete === null) return;

      const key = keyOf(field);
      if (!presets.has(key)) presets.set(key, CHOICE_KINDS.includes(kind) && complete && !touched.has(key));
      if (presets.get(key)) return;

      total += 1;
      if (kind === 'photo') hasPhoto = true;
      if (kind === 'text') hasTyping = true;
      if (complete) {
        done += 1;
        completed.push(field);
      } else {
        missing.push(field);
        if (!firstIncomplete) firstIncomplete = field;
      }
    });

    return { done, total, complete: done === total, firstIncomplete, missing, completed, hasPhoto, hasTyping };
  };

  /* ------------------------------------------------------------------ theme header */

  const getPanel = () => {
    const panel = doc.querySelector(PANEL_SELECTOR);
    return panel && panel.querySelector(FORM_SELECTOR) ? panel : null;
  };

  const ensureHeader = () => {
    const panel = getPanel();
    if (!panel) return null;

    let header = panel.querySelector(`:scope > .${HEADER_CLASS}`);
    if (header && header.querySelector(`.${HEADER_CLASS}__alert`)) return header;
    if (header) header.remove(); // old (2026-10-02) markup from a cached script — rebuild once

    header = doc.createElement('div');
    header.className = HEADER_CLASS;
    header.hidden = true;
    header.innerHTML = `
      <div class="${HEADER_CLASS}__row">
        <h2 class="${HEADER_CLASS}__title" tabindex="-1">${icon('sparkles', `${HEADER_CLASS}__title-icon`)}<span>Make it yours</span></h2>
        <span class="${HEADER_CLASS}__count"><span class="${HEADER_CLASS}__count-ok">${icon('check')}</span><span class="${HEADER_CLASS}__count-text"></span></span>
      </div>
      <div class="${HEADER_CLASS}__bar" role="progressbar" aria-label="Personalization progress" aria-valuemin="0" aria-valuemax="0" aria-valuenow="0"><span></span></div>
      <p class="${HEADER_CLASS}__hint">${icon('info', `${HEADER_CLASS}__hint-icon`)}${icon('check', `${HEADER_CLASS}__hint-icon ${HEADER_CLASS}__hint-icon--ok`)}<span class="${HEADER_CLASS}__hint-text"></span></p>
      <div class="${HEADER_CLASS}__alert" role="alert" hidden>
        ${icon('alert', `${HEADER_CLASS}__alert-icon`)}
        <div class="${HEADER_CLASS}__alert-body">
          <b class="${HEADER_CLASS}__alert-title"></b>
          <span class="${HEADER_CLASS}__alert-text">We've marked them in red below. Fill them in and you're ready to go.</span>
          <button type="button" class="${HEADER_CLASS}__alert-btn">Show me the first one ${icon('down')}</button>
        </div>
      </div>
      <span class="${HEADER_CLASS}__live caf-visually-hidden" aria-live="polite"></span>
    `;
    header.querySelector(`.${HEADER_CLASS}__alert-btn`).addEventListener('click', () => focusFirstIncomplete());
    panel.prepend(header);
    return header;
  };

  // Only write when values change so the MutationObserver below never feeds itself.
  const setText = (node, text) => {
    if (node && node.textContent !== text) node.textContent = text;
  };
  const setHtml = (node, html) => {
    if (node && node.dataset.cafHtml !== html) {
      node.innerHTML = html;
      node.dataset.cafHtml = html;
    }
  };
  const setAttr = (node, name, value) => {
    if (node && node.getAttribute(name) !== value) node.setAttribute(name, value);
  };

  const announce = (message) => {
    const live = doc.querySelector(`.${HEADER_CLASS}__live`);
    if (!live) return;
    live.textContent = '';
    window.setTimeout(() => {
      live.textContent = message;
    }, 60);
  };

  // Platform product, choices only → the heading of the first choice still to make ("Choose Design"), for the
  // sticky bar's button. '' everywhere else: TeeInBlue products keep "Start / Finish personalizing".
  const choiceLabelOf = (state) => {
    if (!isPlatform() || state.hasPhoto || state.hasTyping || !state.firstIncomplete) return '';
    const heading = state.firstIncomplete.querySelector('.tee-field__heading > span:not(.tee-asterisk), .tee-form-label');
    const text = heading ? heading.textContent.replace(/\s+/g, ' ').trim() : '';
    return text && text.length <= 24 ? text : 'Choose your option';
  };

  const escapeHtml = (value) =>
    String(value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const previewLabel = () => {
    const preview = doc.querySelector('#teeFormActions .tee-btn--preview, .caf-pdp-custom-panel .tee-campaign-form-footer .tee-btn--preview');
    return preview && preview.getClientRects().length ? preview.textContent.trim() : '';
  };

  const hintHtml = (state) => {
    if (state.complete) {
      const label = previewLabel();
      return label
        ? `<b>Looks good!</b> Tap <b>${escapeHtml(label)}</b> to see your finished design before adding it to your cart.`
        : '<b>Looks good!</b> Everything is filled in — you are ready to add it to your cart.';
    }
    // Platform product whose campaign only asks for a choice (pick one of the designs): nothing is typed.
    if (isPlatform() && !state.hasPhoto && !state.hasTyping) {
      const label = previewLabel();
      return label
        ? `Pick the design you like. Tap <b>${escapeHtml(label)}</b> to see it full size.`
        : 'Pick the design you like — there is nothing to type.';
    }
    return state.hasPhoto
      ? 'Add your photo and details below. We print <b>exactly</b> what you see — please double-check spelling.'
      : 'About 1 minute. We print <b>exactly</b> what you type — please double-check spelling.';
  };

  /* ------------------------------------------------------------------ generated per-field CSS */

  const cssEscape = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/[^\w-]/g, '\\$&'));

  // Same prefix as the static rules in assets/caf-rd-pdp-personalize.css, plus the field id, so a generated
  // rule always out-ranks the static one it undoes (or switches on).
  const RULE_PREFIX = 'html body.caf-theme.teeinblue-enabled .product__details--product-page #teeFormInputs';
  const CONTROL_LIST = ':is(.tee__input, .tee-field__select, .tee-custom-select__input, .tee-photo-label)';

  let lastCss = null;
  const writeStyle = (state, maxed) => {
    const rules = [];
    // Stale app validation: TeeInBlue keeps `.tee-field--invalid` + its message until it re-validates on the
    // next click. Once our read says the field is complete, drop the red treatment for that field only.
    const fixed = state.completed.filter((field) => field.id).map((field) => `${RULE_PREFIX} #${cssEscape(field.id)}.tee-field--invalid`);
    if (fixed.length) {
      rules.push(`${fixed.map((sel) => `${sel} .tee-required-message`).join(',\n')} { display: none !important; }`);
      rules.push(`${fixed.join(',\n')} { padding-bottom: 0 !important; }`);
      rules.push(
        `${fixed.map((sel) => `${sel} ${CONTROL_LIST}:not(:focus):not(:focus-within)`).join(',\n')} {` +
          ' border-color: var(--caf-pdp-input-border, #e3d6c8) !important; background-color: var(--caf-surface, #ffffff) !important; box-shadow: none !important; }',
      );
    }
    // Max-length reached (v3 state "maxlength"): amber counter + the app's own (otherwise hidden)
    // `.tee-maxlength-warning` shown as a callout under the input while it has focus.
    maxed.forEach(({ field, limit }) => {
      const sel = `${RULE_PREFIX} #${cssEscape(field.id)}`;
      rules.push(`${sel} .tee-field__total-characters { color: var(--caf-pdp-limit-ink, #8a5a00) !important; font-weight: 800 !important; }`);
      if (limit >= MAXLENGTH_CALLOUT_MIN) {
        rules.push(`${sel} .tee-field__input:focus-within ~ .tee-maxlength-warning { display: grid !important; }`);
        rules.push(`${sel} .tee-maxlength-warning::before { content: "That's the ${limit}-character limit."; }`);
      }
    });
    if (state.complete && staleRequiredText && formError() === staleRequiredText) {
      rules.push(
        'html body.caf-theme.teeinblue-enabled .product__details--product-page #teeFormActions .tee-form-responses .tee-error { display: none !important; }',
        `html body.caf-theme.${PLATFORM_CLASS} .product__details--product-page .caf-pdp-custom-panel .tee-form-responses .tee-error { display: none !important; }`,
      );
    }
    const css = rules.join('\n');
    if (css === lastCss) return;
    lastCss = css;
    let style = doc.getElementById(STYLE_ID);
    if (!style) {
      style = doc.createElement('style');
      style.id = STYLE_ID;
      doc.head.appendChild(style);
    }
    style.textContent = css;
  };

  const getMaxed = () =>
    Array.from(doc.querySelectorAll(`${FORM_SELECTOR} .tee-field--text`))
      .map((field) => {
        const input = own(field, 'input[maxlength], textarea[maxlength]')[0];
        const limit = input ? parseInt(input.getAttribute('maxlength'), 10) : 0;
        return { field, input, limit };
      })
      .filter(({ field, input, limit }) => field.id && input && limit > 0 && input.value.length >= limit);

  /* ------------------------------------------------------------------ photo upload veil (v3 "photo-uploading") */

  let veilShown = false;
  const syncPhotoVeil = () => {
    const busy = Boolean(doc.querySelector(`${FORM_SELECTOR} .tee-field--photo input[type="file"]:disabled`));
    const wrap = doc.querySelector('.product__images');
    if (!wrap) return;
    let veil = wrap.querySelector(':scope > .caf-pdp-photo-veil');
    if (!busy && !veil) return;
    if (!veil) {
      veil = doc.createElement('div');
      veil.className = 'caf-pdp-photo-veil';
      veil.hidden = true;
      veil.setAttribute('aria-hidden', 'true');
      const image = doc.querySelector('meta[property="og:image"]');
      if (image && image.content) veil.style.setProperty('--caf-pdp-veil-image', `url("${image.content.replace(/"/g, '%22')}")`);
      veil.innerHTML =
        '<span class="caf-pdp-photo-veil__card"><span class="caf-pdp-photo-veil__spin"></span><b>Adding your photo…</b><span>This takes a few seconds.</span></span>';
      wrap.appendChild(veil);
    }
    if (busy) {
      const gallery = wrap.querySelector('#tee-gallery');
      // Phones: assets/tee-sticky-gallery.js may have pinned the app gallery as a fixed band at the top —
      // then the veil follows it (fixed, same rect) instead of covering the in-flow column.
      const pinned = Boolean(
        gallery &&
          gallery.classList.contains('sticky-image-gallery') &&
          doc.body.classList.contains('gallery--sticky') &&
          !doc.body.classList.contains('gallery--sticky--hide'),
      );
      veil.classList.toggle('is-pinned', pinned);
      if (pinned) {
        const rect = gallery.getBoundingClientRect();
        veil.style.setProperty('--caf-pdp-veil-top', `${Math.round(rect.top)}px`);
        veil.style.setProperty('--caf-pdp-veil-left', `${Math.round(rect.left)}px`);
        veil.style.setProperty('--caf-pdp-veil-w', `${Math.round(rect.width)}px`);
        veil.style.setProperty('--caf-pdp-veil-h', `${Math.round(rect.height)}px`);
      } else {
        const wrapRect = wrap.getBoundingClientRect();
        // the app's square stage — #tee-gallery itself can be taller (TeeInBlue sets an inline height)
        const stage = gallery && gallery.querySelector('.tee-gallery');
        const frame = stage && stage.getClientRects().length ? stage : gallery;
        const rect = frame && frame.getClientRects().length ? frame.getBoundingClientRect() : wrapRect;
        veil.style.setProperty('--caf-pdp-veil-top', `${Math.max(0, Math.round(rect.top - wrapRect.top))}px`);
        veil.style.setProperty('--caf-pdp-veil-h', `${Math.round(Math.min(rect.height, wrapRect.height) || wrapRect.height)}px`);
        // desktop: the live preview sits beside the reserved thumbnail-rail column (CSS §7) — cover only its frame
        veil.style.setProperty('--caf-pdp-veil-x', `${Math.max(0, Math.round(rect.left - wrapRect.left))}px`);
        veil.style.setProperty('--caf-pdp-veil-iw', `${Math.round(Math.min(rect.width, wrapRect.width) || wrapRect.width)}px`);
      }
    }
    if (veil.hidden === busy) veil.hidden = !busy;
    if (busy !== veilShown) {
      veilShown = busy;
      announce(busy ? 'Adding your photo…' : 'Photo added.');
      // the pinned/in-flow geometry changes with scroll while the upload runs
      if (busy) window.addEventListener('scroll', onVeilScroll, { passive: true });
      else window.removeEventListener('scroll', onVeilScroll);
    }
  };

  let veilRaf = false;
  function onVeilScroll() {
    if (veilRaf) return;
    veilRaf = true;
    window.requestAnimationFrame(() => {
      veilRaf = false;
      syncPhotoVeil();
    });
  }

  /* ------------------------------------------------------------------ render */

  let attempted = false;
  // The app's form-level "Some fields are required" line stays until it re-validates on the next click;
  // once every field is filled we hide exactly that stale text (any other/new app message stays visible).
  let staleRequiredText = '';
  let lastKey = '';
  let lastComplete = null;
  let lastMaxedKey = '';

  const render = () => {
    const state = getState();
    const header = ensureHeader();
    const missingCount = state.missing.length;
    if (attempted && !missingCount) attempted = false;
    const showErrors = attempted && missingCount > 0;

    if (header) {
      const hidden = state.total === 0;
      if (header.hidden !== hidden) header.hidden = hidden;
      header.classList.toggle('is-complete', state.complete && state.total > 0);
      header.classList.toggle('has-errors', showErrors);
      setText(header.querySelector(`.${HEADER_CLASS}__count-text`), state.complete ? 'All set' : `${state.done} of ${state.total} done`);

      const bar = header.querySelector(`.${HEADER_CLASS}__bar`);
      const width = `${state.total ? Math.round((state.done / state.total) * 100) : 0}%`;
      const fill = bar && bar.querySelector('span');
      if (fill && fill.style.width !== width) fill.style.width = width;
      setAttr(bar, 'aria-valuemax', String(state.total));
      setAttr(bar, 'aria-valuenow', String(state.done));
      setAttr(bar, 'aria-valuetext', state.complete ? 'All set' : `${state.done} of ${state.total} done`);

      setHtml(header.querySelector(`.${HEADER_CLASS}__hint-text`), hintHtml(state));
      const alert = header.querySelector(`.${HEADER_CLASS}__alert`);
      if (alert && alert.hidden === showErrors) alert.hidden = !showErrors;
      setText(header.querySelector(`.${HEADER_CLASS}__alert-title`), `${missingCount} ${missingCount === 1 ? 'detail' : 'details'} still needed`);
    }

    const panel = getPanel();
    if (panel) panel.toggleAttribute('data-caf-errors', showErrors);

    const maxed = getMaxed();
    writeStyle(state, maxed);
    const maxedKey = maxed.map(({ field }) => field.id).join('|');
    if (maxedKey !== lastMaxedKey) {
      const added = maxed.find(({ field }) => !lastMaxedKey.split('|').includes(field.id));
      lastMaxedKey = maxedKey;
      if (added && added.limit >= MAXLENGTH_CALLOUT_MIN) {
        const label = own(added.field, '.tee-field__heading > span')[0];
        announce(`That's the ${added.limit}-character limit${label ? ` for ${label.textContent.trim()}` : ''}.`);
      }
    }

    syncPhotoVeil();

    if (state.total && lastComplete === false && state.complete && touched.size) {
      announce('All set. You can preview your design or add it to your cart.');
    }
    lastComplete = state.total ? state.complete : null;

    const key = `${state.done}/${state.total}/${missingCount}/${showErrors}`;
    if (key !== lastKey) {
      lastKey = key;
      doc.dispatchEvent(
        new CustomEvent(EVENT_NAME, {
          detail: {
            done: state.done,
            total: state.total,
            complete: state.complete,
            missing: missingCount,
            attempted: showErrors,
            // Platform product, choices only: the sticky bar names the choice instead of "Start personalizing".
            choiceLabel: choiceLabelOf(state),
          },
        }),
      );
    }
  };

  let pending = false;
  const scheduleRender = () => {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(() => {
      pending = false;
      render();
    });
  };

  /* ------------------------------------------------------------------ guidance: scroll + focus */

  // Height of whatever covers the top of the viewport: the global sticky header (published by
  // assets/caf-rd-header.js as --caf-sticky-header-h) plus, on phones, the fixed TeeInBlue sticky preview
  // (assets/tee-sticky-gallery.js) while it is showing.
  const topOffset = () => {
    let offset = parseFloat(window.getComputedStyle(root).getPropertyValue('--caf-sticky-header-h')) || 0;
    if (!offset) {
      const probe = doc.elementFromPoint(window.innerWidth / 2, 2);
      let node = probe;
      while (node && node !== doc.body) {
        const position = window.getComputedStyle(node).position;
        if (position === 'fixed' || position === 'sticky') {
          offset = Math.max(0, node.getBoundingClientRect().bottom);
          break;
        }
        node = node.parentElement;
      }
    }
    const preview = doc.querySelector('body.gallery--sticky:not(.gallery--sticky--hide) #tee-gallery.sticky-image-gallery');
    if (preview) offset = Math.max(offset, preview.getBoundingClientRect().bottom);
    return offset;
  };

  const focusTarget = (field) =>
    own(field, `${TEXT_CONTROL}, select, .tee-custom-select__input, .tee-btn--upload, input[type="radio"], input[type="checkbox"]`).find(
      (control) => !control.classList.contains('tee-custom-select__search-input') && control.getClientRects().length,
    ) || own(field, '.tee-btn--upload, .tee-custom-select__input')[0];

  // While we guide, the app marks fields invalid a few hundred ms later and the page grows; Chrome's scroll
  // anchoring would then shift the page and push the field back under the header. Pause anchoring for the
  // guidance window and re-check the position until the layout has settled.
  let guideTimers = [];
  let anchorReset = 0;
  const pauseScrollAnchoring = (ms) => {
    root.style.setProperty('overflow-anchor', 'none');
    window.clearTimeout(anchorReset);
    anchorReset = window.setTimeout(() => root.style.removeProperty('overflow-anchor'), ms);
  };

  const scrollFieldIntoPlace = (field, behavior) => {
    const top = field.getBoundingClientRect().top + window.scrollY - topOffset() - 16;
    window.scrollTo({ top: Math.max(0, top), behavior });
  };

  const focusFirstIncomplete = () => {
    const { firstIncomplete } = getState();
    if (!firstIncomplete) return false;

    guideTimers.forEach((timer) => window.clearTimeout(timer));
    pauseScrollAnchoring(2200);
    scrollFieldIntoPlace(firstIncomplete, reduceMotion.matches ? 'auto' : 'smooth');
    guideTimers = [
      window.setTimeout(() => {
        const control = focusTarget(firstIncomplete);
        if (control && typeof control.focus === 'function') control.focus({ preventScroll: true });
      }, reduceMotion.matches ? 0 : 420),
      ...[700, 1200, 1900].map((delay) =>
        window.setTimeout(() => {
          if (!firstIncomplete.isConnected) return;
          const rectTop = firstIncomplete.getBoundingClientRect().top;
          const want = topOffset() + 16;
          if (rectTop < want - 4 || rectTop > want + 72) scrollFieldIntoPlace(firstIncomplete, reduceMotion.matches ? 'auto' : 'smooth');
        }, delay),
      ),
    ];
    return true;
  };

  const markAttempt = () => {
    const state = getState();
    if (!state.total || state.complete) return false;
    attempted = true;
    render();
    return true;
  };

  // ATC / Buy now with missing fields: TeeInBlue validates (marks fields, focuses the first invalid input,
  // which scrolls it UNDER the sticky header). We run after it and place the first missing field below the
  // header instead. Capture phase so we read the state before the app re-renders.
  const formError = () => {
    const node = doc.querySelector('#teeFormActions .tee-form-responses .tee-error, .caf-pdp-custom-panel .tee-form-responses .tee-error');
    return node ? node.textContent.trim() : '';
  };

  const onAttemptClick = (event) => {
    const button = event.target instanceof Element && event.target.closest(ATTEMPT_BUTTONS);
    if (!button || !doc.querySelector(PANEL_SELECTOR)) return;
    if (!markAttempt()) return;
    // Wait until the app has validated (its error line appears / it moved focus), then correct the scroll.
    const before = doc.activeElement;
    let tries = 0;
    const settle = () => {
      tries += 1;
      const appDone = formError() || (doc.activeElement !== before && doc.activeElement !== doc.body);
      if (!appDone && tries < 10) {
        window.setTimeout(settle, 100);
        return;
      }
      staleRequiredText = formError();
      focusFirstIncomplete();
      scheduleRender();
    };
    window.setTimeout(settle, 120);
  };

  /* ------------------------------------------------------------------ dropdown "no matches" hint */

  let ddTimer = 0;
  const onSearchInput = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains('tee-custom-select__search-input')) return;
    window.clearTimeout(ddTimer);
    ddTimer = window.setTimeout(() => {
      const panel = getPanel();
      const value = input.value.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 40);
      if (panel) {
        if (value) panel.style.setProperty('--caf-pdp-dd-query', JSON.stringify(`“${value}”`));
        else panel.style.removeProperty('--caf-pdp-dd-query');
        panel.toggleAttribute('data-caf-dd-query', Boolean(value));
      }
      const select = input.closest('.tee-custom-select');
      const list = select && select.querySelector('.tee-custom-select__list');
      if (value && list && !list.querySelector('li')) announce(`No options match “${value}”. Check the spelling, or try part of the name.`);
    }, 160);
  };

  /* ------------------------------------------------------------------ typing flag (mobile sticky bar + chat step aside) */

  const isTextField = (node) =>
    node instanceof HTMLElement &&
    node.matches('input:not([type="radio"]):not([type="checkbox"]):not([type="file"]):not([type="button"]):not([type="submit"]), textarea') &&
    Boolean(node.closest(FORM_SELECTOR));

  const syncTyping = () => {
    const typing = mqMobile.matches && isTextField(doc.activeElement);
    if (root.classList.contains('caf-pdp-typing') !== typing) root.classList.toggle('caf-pdp-typing', typing);
  };

  /* ------------------------------------------------------------------ public API + init */

  window.CAFPdpPersonalizeProgress = {
    getState: () => {
      const state = getState();
      const { done, total, complete, missing } = state;
      return { done, total, complete, missing: missing.length, choiceLabel: choiceLabelOf(state) };
    },
    focusFirstIncomplete,
    markAttempt,
    refresh: scheduleRender,
  };

  const init = () => {
    const details = doc.querySelector(DETAILS_SELECTOR);
    if (!details) return;

    render();
    [300, 1000, 2500, 5000].forEach((delay) => window.setTimeout(scheduleRender, delay));

    // TeeInBlue updates conditional fields asynchronously after input; re-read once more shortly after.
    const onFieldEvent = (event) => {
      markTouched(event);
      scheduleRender();
      window.setTimeout(scheduleRender, 250);
    };
    ['input', 'change', 'click'].forEach((type) => doc.addEventListener(type, onFieldEvent, true));
    ['pointerdown', 'keydown'].forEach((type) => doc.addEventListener(type, markTouched, true));
    doc.addEventListener('click', onAttemptClick, true);
    doc.addEventListener('input', onSearchInput, true);
    doc.addEventListener('focusin', syncTyping);
    doc.addEventListener('focusout', () => window.setTimeout(syncTyping, 0));
    doc.addEventListener(TEEINBLUE_EVENT_COMPONENT_INJECTED, scheduleRender);

    // Only changes to the TeeInBlue form matter. Other things in the product details change all the time (review
    // badge, price app, countdown); re-reading every field on each of those cost ~1s of main thread per 3s on a
    // phone and delayed taps and keystrokes (INP, measured 2026-10-12).
    const FORM_PARTS = `${FORM_SELECTOR}, #teeFormActions`;
    const isFormNode = (node) => node instanceof Element && (node.matches(FORM_PARTS) || Boolean(node.querySelector(FORM_PARTS)));
    const touchesForm = (mutation) => {
      const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
      if (target && target.closest(`.${HEADER_CLASS}`)) return false; // our own header
      if (target && target.closest(FORM_PARTS)) {
        return mutation.type === 'attributes'
          ? mutation.target instanceof HTMLInputElement
          : mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
      }
      // The form itself (or a wrapper that holds it) was injected or removed.
      return Array.from(mutation.addedNodes).some(isFormNode) || Array.from(mutation.removedNodes).some(isFormNode);
    };
    const observer = new MutationObserver((mutations) => {
      if (mutations.some(touchesForm)) scheduleRender();
    });
    observer.observe(details, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
